import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, qs, qsa } from '../dom.js';
import { icon } from '../icons.js';
import { importStudentsDialog } from '../studentImport.js';
import { studentFormDialog } from '../studentForm.js';
import type { StudentRecord } from '../../db/schema.js';

export async function studentsView(): Promise<RenderedView> {
  const [students, classes] = await Promise.all([repos.allStudents(), repos.listClasses()]);
  const className = new Map(classes.map((c) => [c.id, c.name]));

  const rowHtml = (s: StudentRecord) =>
    `<button class="row" data-student="${esc(s.id)}" data-search="${esc((s.name + ' ' + s.lrn).toLowerCase())}" data-class="${esc(s.classId)}">
      <span class="grow"><span class="ttl">${esc(s.name)}</span>
      <span class="meta">${esc(className.get(s.classId) ?? '—')} · LRN <span class="mono">${esc(s.lrn)}</span></span></span><span class="chev">${icon.edit()}</span></button>`;

  const chip = (id: string, label: string) =>
    `<button class="chip" data-filter="${esc(id)}"><span class="chip-dot"></span>${esc(label)}</button>`;
  const chips = [chip('', 'All classes'), ...classes.map((c) => chip(c.id, c.name))].join('');

  const list = students.length
    ? `<ul class="list" id="roster">${students.map(rowHtml).join('')}</ul>`
    : `<div class="empty">${icon.user()}<p>No students yet.</p></div>`;

  return {
    title: 'Students',
    tab: 'students',
    html: `<div class="view">
      <div class="row2"><button class="btn btn-primary" data-add>${icon.plus()} Add student</button>
        <button class="btn btn-ghost" data-import>${icon.doc()} Import CSV</button></div>
      <div class="searchbar">${icon.search()}<input class="input" id="q" placeholder="Search name or LRN" autocomplete="off"></div>
      <div class="chips" id="chips">${chips}</div>
      ${list}
      <p class="empty" id="nomatch" hidden>No students match.</p>
    </div>`,
    mount(root) {
      let activeClass = '';
      const apply = () => {
        const term = qs<HTMLInputElement>(root, '#q')!.value.trim().toLowerCase();
        let shown = 0;
        qsa<HTMLElement>(root, '#roster .row').forEach((el) => {
          const okClass = !activeClass || el.dataset.class === activeClass;
          const okTerm = !term || (el.dataset.search ?? '').includes(term);
          const show = okClass && okTerm;
          el.style.display = show ? '' : 'none';
          if (show) shown++;
        });
        const none = qs<HTMLElement>(root, '#nomatch');
        if (none) none.hidden = shown !== 0 || students.length === 0;
      };
      qs<HTMLInputElement>(root, '#q')?.addEventListener('input', apply);
      onClick(root, '.chip', (el) => {
        activeClass = el.dataset.filter ?? '';
        qsa<HTMLElement>(root, '.chip').forEach((c) => c.classList.toggle('on', c === el));
        apply();
      });
      root.querySelector('.chip')?.classList.add('on'); // "All" selected by default
      onClick(root, '[data-add]', () => studentFormDialog({ classes, onDone: () => navigate('#/students') }));
      onClick(root, '[data-import]', () => importStudentsDialog());
      onClick(root, '[data-student]', (el) => navigate(`#/student/${el.dataset.student}`));
    },
  };
}
